// apps/web/src/stores/outreach.ts
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { outreachApi, type OutreachCampaign, type OutreachThreadRow, type OutreachMessage, type OutreachTag, type ConvertToDealPayload, type OutreachSendStatus, type SendDirectResult } from '@/lib/outreach-api'
import { useNotificationStore } from './notifications'

export const useOutreachStore = defineStore('outreach', () => {
  const campaigns = ref<OutreachCampaign[]>([])
  const currentCampaign = ref<OutreachCampaign | null>(null)
  const threads = ref<OutreachThreadRow[]>([])
  const unmatched = ref<OutreachMessage[]>([])
  const currentThread = ref<{ enrollment: any; contact: any; messages: OutreachMessage[]; dealId?: string | null } | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const notify = useNotificationStore()

  function fail(err: any, msg: string) {
    error.value = err?.response?.data?.error?.message || err?.response?.data?.message || msg
    notify.error(error.value || msg)
  }

  async function fetchCampaigns(params?: Record<string, unknown>) {
    loading.value = true; error.value = null
    try { const r = await outreachApi.listCampaigns(params); campaigns.value = r.data }
    catch (e) { fail(e, 'Failed to load campaigns') } finally { loading.value = false }
  }
  async function fetchCampaign(id: string) {
    loading.value = true; error.value = null
    try { const r = await outreachApi.getCampaign(id); currentCampaign.value = r.data; return r.data }
    catch (e) { fail(e, 'Failed to load campaign'); return null } finally { loading.value = false }
  }
  async function createCampaign(data: Partial<OutreachCampaign>) {
    try { const r = await outreachApi.createCampaign(data); notify.success('Campaign created'); return r.data }
    catch (e) { fail(e, 'Failed to create campaign'); throw e }
  }
  async function updateCampaign(id: string, data: Partial<OutreachCampaign>) {
    try { const r = await outreachApi.updateCampaign(id, data); notify.success('Saved'); return r.data }
    catch (e) { fail(e, 'Failed to save campaign'); throw e }
  }
  async function previewRecipients(id: string) {
    try { const r = await outreachApi.previewRecipients(id); return r.data.count } catch (e) { fail(e, 'Preview failed'); return 0 }
  }
  async function fetchTags(q?: string): Promise<OutreachTag[]> {
    try { const r = await outreachApi.listTags(q); return r.data } catch { return [] }
  }
  async function fetchRecipients(id: string, params?: Record<string, unknown>) {
    try { return await outreachApi.campaignRecipients(id, params) }
    catch (e) { fail(e, 'Failed to load recipients'); return { data: [] as OutreachThreadRow[], meta: { page: 1, pageSize: 50, total: 0, hasNext: false } } }
  }
  async function testSend(id: string) {
    try { await outreachApi.testSend(id); notify.success('Test email sent') } catch (e) { fail(e, 'Test send failed') }
  }
  async function sendCampaign(id: string) {
    try { const r = await outreachApi.sendCampaign(id); notify.success(`Queued ${r.data.queued} email(s)`); return r.data }
    catch (e) { fail(e, 'Send failed'); throw e }
  }
  async function pauseCampaign(id: string) { try { await outreachApi.pauseCampaign(id); notify.success('Paused') } catch (e) { fail(e, 'Pause failed') } }
  async function resumeCampaign(id: string) { try { await outreachApi.resumeCampaign(id); notify.success('Resumed') } catch (e) { fail(e, 'Resume failed') } }

  async function fetchThreads(params?: Record<string, unknown>) {
    loading.value = true; error.value = null
    try { const r = await outreachApi.listThreads(params); threads.value = r.data } catch (e) { fail(e, 'Failed to load inbox') } finally { loading.value = false }
  }
  async function fetchUnmatched(params?: Record<string, unknown>) {
    loading.value = true; error.value = null
    try { const r = await outreachApi.listUnmatched(params); unmatched.value = r.data } catch (e) { fail(e, 'Failed to load unmatched mail') } finally { loading.value = false }
  }
  async function fetchThread(enrollmentId: string) {
    loading.value = true; error.value = null
    try { const r = await outreachApi.getThread(enrollmentId); currentThread.value = r.data; return r.data } catch (e) { fail(e, 'Failed to load thread'); return null } finally { loading.value = false }
  }
  async function reply(enrollmentId: string, bodyHtml: string) {
    try { await outreachApi.reply(enrollmentId, bodyHtml); notify.success('Reply sent'); await fetchThread(enrollmentId) } catch (e) { fail(e, 'Reply failed') }
  }
  async function convertToDeal(enrollmentId: string, payload: ConvertToDealPayload) {
    try {
      const r = await outreachApi.convertToDeal(enrollmentId, payload)
      const dealId = r.data._id
      // Persist the new link locally so the inbox flips to "View deal" without a refetch.
      const row = threads.value.find(t => t.enrollmentId === enrollmentId)
      if (row) row.dealId = dealId
      if (currentThread.value && currentThread.value.enrollment?._id === enrollmentId) currentThread.value.dealId = dealId
      notify.success('Deal created')
      return r.data
    } catch (e) { fail(e, 'Failed to create deal'); throw e }
  }

  // --- Direct (custom) email ---
  async function fetchSendStatus(): Promise<OutreachSendStatus | null> {
    try { const r = await outreachApi.sendStatus(); return r.data } catch (e) { fail(e, 'Failed to load send status'); return null }
  }
  async function sendDirect(contactId: string, payload: { subject: string; bodyHtml: string; override?: boolean; draftId?: string }): Promise<SendDirectResult | null> {
    try {
      const r = await outreachApi.sendDirect(contactId, payload)
      const res = r.data
      if (res.status === 'sent') notify.success('Email sent')
      else if (res.status === 'queued') notify.success('Email queued — it will send shortly')
      else if (res.status === 'test') notify.info('Test mode — logged to the server console, not sent')
      return res
    } catch (e) { fail(e, 'Failed to send email'); return null }
  }

  async function saveDraftEmail(contactId: string, payload: { subject: string; bodyHtml: string; draftId?: string }): Promise<OutreachMessage | null> {
    try {
      const r = await outreachApi.saveDraft(contactId, payload)
      notify.success('Draft saved')
      return r.data
    } catch (e) { fail(e, 'Failed to save draft'); return null }
  }

  async function deleteDraftEmail(draftId: string): Promise<boolean> {
    try {
      await outreachApi.deleteDraft(draftId)
      notify.success('Draft deleted')
      return true
    } catch (e) { fail(e, 'Failed to delete draft'); return false }
  }

  return {
    campaigns, currentCampaign, threads, unmatched, currentThread, loading, error,
    fetchCampaigns, fetchCampaign, createCampaign, updateCampaign, previewRecipients,
    fetchTags, fetchRecipients,
    testSend, sendCampaign, pauseCampaign, resumeCampaign,
    fetchThreads, fetchUnmatched, fetchThread, reply, convertToDeal,
    fetchSendStatus, sendDirect, saveDraftEmail, deleteDraftEmail,
  }
})
